import { useState, useMemo } from 'react';
import shortid from 'shortid';
import { Box, Button, Modal, Select, Text } from '@rsos/base-components';
import {
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@rsos/base-components/Modal/ModalComponents';
import {
  useCancelTransportMutation,
  type EtlTransportResponse,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import {
  trackCancelAirAmbulanceRequest,
  trackCancelAirAmbulanceRequestApiResponse,
} from '../../../gainsightTrackings/airAmbulanceTrackings';
import { CANCEL_REQUEST_REASONS } from '../AirAmbulanceForm/constants';
import {
  getErrorMessage,
  getErrorStatusCode,
  getSubmissionErrorMessage,
} from '../AirAmbulanceForm/utils';
import type { SelectOption } from '../types';

interface AirAmbulanceEtlCancelModalProps {
  transport: EtlTransportResponse;
  showModal: boolean;
  onClose: () => void;
  onCancelSubmitted?: () => void;
}

function AirAmbulanceEtlCancelModal({
  transport,
  showModal,
  onClose,
  onCancelSubmitted,
}: AirAmbulanceEtlCancelModalProps): JSX.Element {
  const [cancelReason, setCancelReason] = useState<SelectOption | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelTransport] = useCancelTransportMutation();
  const UUID = useMemo(() => shortid.generate(), []);

  const onSelectChange = (selectedOption: SelectOption | null): void => {
    setCancelReason(selectedOption);
  };

  // Reset transient state so a stale error/reason doesn't persist when the
  // modal is reopened.
  const handleClose = (): void => {
    setCancelError(null);
    setCancelReason(null);
    onClose();
  };

  const onConfirmCancel = async (): Promise<void> => {
    if (!cancelReason) return;

    // Clear any error from a previous cancel attempt.
    setCancelError(null);

    trackCancelAirAmbulanceRequest({
      requestId: transport.gmr_transport_id,
      reason: cancelReason.value,
    });

    try {
      await cancelTransport({
        transportId: transport.id,
        reason: cancelReason.value,
      }).unwrap();

      trackCancelAirAmbulanceRequestApiResponse({
        requestId: transport.gmr_transport_id,
        status: 200,
        error: false,
        success: true,
        UUID,
      });

      onCancelSubmitted?.();
      handleClose();
    } catch (error) {
      const statusCode = getErrorStatusCode(error);

      trackCancelAirAmbulanceRequestApiResponse({
        requestId: transport.gmr_transport_id,
        status: statusCode,
        error: getErrorMessage(error),
        success: false,
        UUID,
      });

      // Surface a user-facing error so the modal can render an error box and
      // stay open for the user to retry.
      setCancelError(getSubmissionErrorMessage(statusCode));

      // eslint-disable-next-line no-console -- track errors in sentry
      console.error('Cancel transport error:', error);
    }
  };

  return (
    <Modal
      id="air-ambulance-etl-cancel-request-modal"
      isOpened={showModal}
      onClose={handleClose}
      padding="45px 45px 35px"
      width={400}
    >
      <ModalHeader onClose={handleClose}>
        <Text color="primaryText" size="large">
          Cancel Air Ambulance Request?
        </Text>
      </ModalHeader>
      <ModalBody>
        <Text color="secondaryText" size="small">
          Are you sure you want to cancel the request for air ambulance?
        </Text>
        <Box mt={2}>
          <Select
            options={CANCEL_REQUEST_REASONS}
            value={cancelReason}
            onChange={onSelectChange}
            placeholder="Select a cancellation reason (required)"
          />
        </Box>
      </ModalBody>
      {cancelError && (
        <Box mb={1} p={3} bg="lightDanger" borderRadius="4px" role="alert">
          <Text color="background" size="small">
            {cancelError}
          </Text>
        </Box>
      )}
      <ModalFooter>
        <Box horizontal justify="center" width="100%">
          <Button grow btnType="tertiary" mx={0} onClick={handleClose}>
            Keep Request
          </Button>
          <Button
            grow
            btnType="warning_tertiary"
            ml={2}
            onClick={onConfirmCancel}
            disabled={!cancelReason}
          >
            Cancel Request
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
}

export default AirAmbulanceEtlCancelModal;
